const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

/**
 * Wraps a dynamic import function with retry logic to handle transient ChunkLoadErrors.
 * Retries the import up to MAX_RETRIES times with exponential backoff before failing.
 *
 * Use this with next/dynamic directly at the call site (Next.js requires dynamic()
 * options to be an object literal, so we can't wrap dynamic() in a utility).
 *
 * @example
 * import dynamic from "next/dynamic";
 * import { withRetry } from "@/lib/helpers/dynamicRetryImport";
 *
 * const MyComponent = dynamic(withRetry(() => import("@/components/MyComponent")));
 * const MyComponent = dynamic(withRetry(() => import("@/components/MyComponent")), { ssr: false });
 * const MyComponent = dynamic(
 *   withRetry(() => import("@/components/MyComponent").then((mod) => mod.NamedExport)),
 *   { ssr: false },
 * );
 */
export function withRetry<T>(importFn: () => T): () => T {
  return () => {
    let attempts = 0;

    const attempt = (): T =>
      (importFn() as Promise<unknown>).catch((error: unknown) => {
        attempts++;
        if (
          error instanceof Error &&
          error.name === "ChunkLoadError" &&
          attempts < MAX_RETRIES
        ) {
          return new Promise((resolve) =>
            setTimeout(resolve, RETRY_DELAY_MS * attempts),
          ).then(attempt);
        }
        throw error;
      }) as T;

    return attempt();
  };
}
